import { useState, useEffect } from "react";
import { QRCodeCanvas } from "qrcode.react";
import {
  Globe, Link2, QrCode, Share2, Smartphone, Play, Apple, ListChecks,
  Check, Copy, Loader2, Rocket, Download, ExternalLink, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { saveCBDeployment } from "../lib/api";
import { copyToClipboard } from "../lib/clipboard";

const DEPLOY_TYPES = [
  { id: "web_app", icon: Globe, title: "Web App", desc: "Deploy as a live web application." },
  { id: "shareable_link", icon: Link2, title: "Shareable App Link", desc: "Generate & copy your live project URL." },
  { id: "qr_code", icon: QrCode, title: "QR Code", desc: "A QR code linked to your live app." },
  { id: "social_media", icon: Share2, title: "Social Media Link", desc: "Share to Instagram, Facebook, TikTok & WhatsApp." },
  { id: "pwa", icon: Smartphone, title: "PWA Setup", desc: "Let customers add the web app to their home screen." },
  { id: "google_play", icon: Play, title: "Google Play", desc: "Prepare for Store Submission — Android files & requirements.", store: true },
  { id: "apple_app_store", icon: Apple, title: "Apple App Store", desc: "Prepare for Store Submission — iOS files & requirements.", store: true },
];

const CHECKLIST_ITEMS = [
  "App icons (all required sizes)",
  "Screenshots for each device size",
  "Privacy policy URL",
  "App name, description & keywords",
  "Category & content rating",
  "Support & marketing URLs",
];

const STATUS_META = {
  draft: { label: "Draft", cls: "bg-[#F0F0F0] text-[#687280]" },
  ready: { label: "Ready", cls: "bg-amber-100 text-amber-700" },
  deployed: { label: "Deployed", cls: "bg-emerald-100 text-emerald-700" },
};

export const DeploymentCenter = ({ project, onUpdated }) => {
  const dep = project.deployment || { live_url: "", types: [], checklist: [], status: "draft" };
  const [liveUrl, setLiveUrl] = useState(dep.live_url || "");
  const [types, setTypes] = useState(dep.types || []);
  const [checklist, setChecklist] = useState(dep.checklist || []);
  const [status, setStatus] = useState(dep.status || "draft");
  const [saving, setSaving] = useState(false);

  // Re-sync local fields whenever the saved deployment changes (e.g. after a save/launch).
  useEffect(() => {
    const d = project.deployment || {};
    setLiveUrl(d.live_url || "");
    setTypes(d.types || []);
    setChecklist(d.checklist || []);
    setStatus(d.status || "draft");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.deployment?.updated_at]);

  const validUrl = /^https?:\/\/.+/.test(liveUrl.trim());

  const toggle = (arr, setArr, id) =>
    setArr(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const persist = async (overrides = {}) => {
    setSaving(true);
    try {
      const body = { live_url: liveUrl.trim(), types, checklist, status, ...overrides };
      const updated = await saveCBDeployment(body);
      onUpdated?.(updated);
      toast.success("Deployment saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save deployment");
    } finally {
      setSaving(false);
    }
  };

  const copy = async (text, label) => {
    const ok = await copyToClipboard(text);
    if (ok) toast.success(`${label} copied`);
    else toast.error("Could not copy. Please copy manually.");
  };

  const shareText = encodeURIComponent(`Check out ${project.project_name}! `);
  const encUrl = encodeURIComponent(liveUrl.trim());

  return (
    <div className="mt-5 rounded-2xl border border-[#D4AF37]/40 bg-white p-5 sm:p-6" data-testid="deployment-center">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-[#0F0F0F] text-[#D4AF37] flex items-center justify-center"><Rocket size={18} /></div>
          <div>
            <h4 className="font-display font-bold text-[#0F0F0F]">Deployment Center</h4>
            <p className="text-xs text-[#687280]">Choose how you want to launch your app.</p>
          </div>
        </div>
        <span className={`text-[11px] font-bold uppercase tracking-wide rounded-full px-2.5 py-1 ${STATUS_META[status].cls}`} data-testid="deploy-status-badge">
          {STATUS_META[status].label}
        </span>
      </div>

      {/* Live URL */}
      <div className="mt-5">
        <label className="text-xs font-semibold text-[#0F0F0F]">Live app URL</label>
        <div className="flex gap-2 mt-1.5">
          <input
            value={liveUrl}
            onChange={(e) => setLiveUrl(e.target.value)}
            placeholder="https://yourapp.com"
            className="flex-1 rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37]"
            data-testid="deploy-live-url"
          />
          <button onClick={() => persist()} disabled={saving} className="inline-flex items-center gap-1.5 bg-[#0F0F0F] text-white rounded-xl px-4 text-sm font-semibold hover:bg-[#1c1c1c] disabled:opacity-60" data-testid="deploy-save-url">
            {saving ? <Loader2 size={15} className="animate-spin" /> : "Save"}
          </button>
        </div>
        <p className="text-[11px] text-[#9CA3AF] mt-1.5">Paste your deployed app URL to power your shareable link, QR code and social sharing.</p>
      </div>

      {/* Choose deployment type */}
      <p className="text-xs font-bold uppercase tracking-wide text-[#9CA3AF] mt-6 mb-3">Choose Deployment Type</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {DEPLOY_TYPES.map((t) => {
          const on = types.includes(t.id);
          return (
            <button
              key={t.id}
              onClick={() => toggle(types, setTypes, t.id)}
              className={`text-left rounded-xl border p-3.5 transition-all ${on ? "border-[#D4AF37] bg-[#FFFDF5]" : "border-gray-200 bg-white hover:border-gray-300"}`}
              data-testid={`deploy-type-${t.id}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${on ? "bg-[#D4AF37] text-[#0F0F0F]" : "bg-[#F7F7F7] text-[#687280]"}`}>
                  <t.icon size={17} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="font-semibold text-sm text-[#0F0F0F]">{t.title}</p>
                    {on && <Check size={13} className="text-[#D4AF37]" />}
                  </div>
                  <p className="text-xs text-[#687280] mt-0.5">{t.desc}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Shareable link + QR */}
      {validUrl && (types.includes("shareable_link") || types.includes("qr_code") || types.includes("social_media")) && (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {(types.includes("shareable_link") || types.includes("social_media")) && (
            <div className="rounded-xl bg-[#F7F7F7] p-4">
              <p className="text-xs font-semibold text-[#0F0F0F] flex items-center gap-1.5"><Link2 size={13} className="text-[#D4AF37]" /> Shareable Link</p>
              <div className="flex items-center gap-2 mt-2">
                <code className="flex-1 truncate text-xs bg-white rounded-lg border border-gray-200 px-2.5 py-2 text-[#374151]">{liveUrl.trim()}</code>
                <button onClick={() => copy(liveUrl.trim(), "Link")} className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50" data-testid="deploy-copy-link"><Copy size={14} /></button>
                <a href={liveUrl.trim()} target="_blank" rel="noreferrer" className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50"><ExternalLink size={14} /></a>
              </div>
              {types.includes("social_media") && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  <SocialBtn label="WhatsApp" href={`https://wa.me/?text=${shareText}${encUrl}`} />
                  <SocialBtn label="Facebook" href={`https://www.facebook.com/sharer/sharer.php?u=${encUrl}`} />
                  <SocialBtn label="X" href={`https://twitter.com/intent/tweet?text=${shareText}&url=${encUrl}`} />
                  <button onClick={() => copy(liveUrl.trim(), "Link for Instagram / TikTok")} className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium hover:bg-gray-50" data-testid="deploy-copy-social">Copy for IG / TikTok</button>
                </div>
              )}
            </div>
          )}
          {types.includes("qr_code") && (
            <div className="rounded-xl bg-[#F7F7F7] p-4 flex flex-col items-center justify-center" data-testid="deploy-qr">
              <div className="bg-white p-3 rounded-xl border border-gray-200">
                <QRCodeCanvas id="cb-qr" value={liveUrl.trim()} size={120} fgColor="#0F0F0F" level="M" />
              </div>
              <button onClick={downloadQr} className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-[#0F0F0F] hover:text-[#B8901F]" data-testid="deploy-download-qr">
                <Download size={13} /> Download QR
              </button>
            </div>
          )}
        </div>
      )}

      {/* PWA */}
      {types.includes("pwa") && (
        <div className="mt-4 rounded-xl border border-gray-200 p-4" data-testid="deploy-pwa">
          <p className="text-sm font-semibold text-[#0F0F0F] flex items-center gap-1.5"><Smartphone size={15} className="text-[#D4AF37]" /> PWA Setup</p>
          <p className="text-xs text-[#687280] mt-1">Your web app can be installed to the home screen. On iOS: Share → “Add to Home Screen”. On Android: the browser will prompt to install. Make sure your app has a manifest and icons.</p>
        </div>
      )}

      {/* Store submission */}
      {(types.includes("google_play") || types.includes("apple_app_store")) && (
        <div className="mt-4 space-y-3">
          {types.includes("google_play") && (
            <StoreCard icon={Play} title="Google Play — Prepare for Store Submission"
              points={["Android build files & requirements prepared", "Store listing assets & metadata", "Submission remains subject to your Google Play developer account & review"]} />
          )}
          {types.includes("apple_app_store") && (
            <StoreCard icon={Apple} title="Apple App Store — Prepare for Store Submission"
              points={["iOS build files & requirements prepared", "Store listing assets & metadata", "Submission remains subject to your Apple developer account & review"]} />
          )}

          {/* Store readiness checklist */}
          <div className="rounded-xl border border-gray-200 p-4">
            <p className="text-sm font-semibold text-[#0F0F0F] flex items-center gap-1.5 mb-2.5"><ListChecks size={15} className="text-[#D4AF37]" /> Store Readiness Checklist</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {CHECKLIST_ITEMS.map((c) => {
                const on = checklist.includes(c);
                return (
                  <button key={c} onClick={() => toggle(checklist, setChecklist, c)} className="flex items-center gap-2 text-left text-sm text-[#374151] py-1" data-testid={`deploy-check-${c.slice(0, 6)}`}>
                    <span className={`w-4 h-4 rounded flex items-center justify-center border ${on ? "bg-[#D4AF37] border-[#D4AF37]" : "border-gray-300"}`}>{on && <Check size={11} className="text-[#0F0F0F]" />}</span>
                    {c}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Status + launch */}
      <div className="mt-6 rounded-xl bg-[#0F0F0F] p-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs text-[#A1A1AA]">Deployment Status</p>
          <div className="flex gap-1.5 mt-2">
            {["draft", "ready", "deployed"].map((s) => (
              <button key={s} onClick={() => setStatus(s)} className={`text-xs font-semibold rounded-full px-3 py-1.5 transition-colors ${status === s ? "bg-[#D4AF37] text-[#0F0F0F]" : "bg-white/10 text-[#A1A1AA] hover:bg-white/15"}`} data-testid={`deploy-status-${s}`}>
                {STATUS_META[s].label}
              </button>
            ))}
          </div>
        </div>
        <button onClick={() => persist({ status: "deployed" })} disabled={saving} className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-6 py-3 text-sm font-bold hover:bg-[#c5a030] disabled:opacity-60" data-testid="deploy-launch-btn">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <><Rocket size={16} /> Launch Project</>}
        </button>
      </div>

      <button onClick={() => persist()} disabled={saving} className="mt-3 w-full rounded-full border border-gray-200 py-2.5 text-sm font-semibold text-[#0F0F0F] hover:bg-[#F7F7F7] disabled:opacity-60" data-testid="deploy-save-all">
        {saving ? "Saving…" : "Save deployment settings"}
      </button>
    </div>
  );
};

const downloadQr = () => {
  const canvas = document.getElementById("cb-qr");
  if (!canvas) return;
  const link = document.createElement("a");
  link.download = "app-qr-code.png";
  link.href = canvas.toDataURL("image/png");
  link.click();
};

const SocialBtn = ({ label, href }) => (
  <a href={href} target="_blank" rel="noreferrer" className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium hover:bg-gray-50 transition-colors">{label}</a>
);

const StoreCard = ({ icon: Icon, title, points }) => (
  <div className="rounded-xl border border-gray-200 p-4">
    <p className="text-sm font-semibold text-[#0F0F0F] flex items-center gap-1.5"><Icon size={15} className="text-[#D4AF37]" /> {title}</p>
    <ul className="mt-2 space-y-1">
      {points.map((p, i) => (
        <li key={i} className="text-xs text-[#687280] flex items-start gap-1.5"><ShieldCheck size={12} className="text-[#D4AF37] mt-0.5 shrink-0" /> {p}</li>
      ))}
    </ul>
  </div>
);
